<template>
    <div class="max-w-4xl mx-auto p-4">
        <h1 class="text-2xl font-semibold text-theme-base mb-2">
            Configuración
        </h1>
        <p class="text-sm text-theme-muted mb-6">
            Cada tarjeta agrupa un aspecto puntual de tu negocio. Entra a la que
            necesites ajustar.
        </p>

        <input
            v-model="search"
            type="search"
            placeholder="Buscar configuración…"
            class="w-full mb-6 rounded-lg border border-theme-border bg-theme-input px-4 py-2 text-sm text-theme-base placeholder-theme-muted"
        />

        <div
            v-if="filteredItems.length"
            class="grid grid-cols-1 sm:grid-cols-2 gap-4"
        >
            <template v-for="item in filteredItems">
                <button
                    v-if="item.locked"
                    :key="item.url"
                    type="button"
                    class="bg-theme-card rounded-xl p-5 flex gap-4 items-start border border-theme-border hover:border-theme-primary transition text-left opacity-70"
                    @click="openUpgrade(item)"
                >
                    <span class="text-2xl leading-none">{{ item.icon }}</span>
                    <span class="flex-1">
                        <span class="block font-medium text-theme-base">
                            {{ item.name }}
                        </span>
                        <span class="block text-sm text-theme-muted mt-1">
                            {{ item.description }}
                        </span>
                        <span
                            class="block text-xs font-medium text-theme-muted mt-2"
                        >
                            🔒 No incluida en tu plan
                        </span>
                    </span>
                </button>
                <router-link
                    v-else
                    :key="item.url"
                    :to="item.url"
                    class="bg-theme-card rounded-xl p-5 flex gap-4 items-start border border-theme-border hover:border-theme-primary transition"
                >
                    <span class="text-2xl leading-none">{{ item.icon }}</span>
                    <span>
                        <span class="block font-medium text-theme-base">
                            {{ item.name }}
                        </span>
                        <span class="block text-sm text-theme-muted mt-1">
                            {{ item.description }}
                        </span>
                    </span>
                </router-link>
            </template>
        </div>
        <p v-else class="text-sm text-theme-muted">
            {{
                visibleItems.length
                    ? "No hay configuraciones que coincidan con tu búsqueda."
                    : "No tienes permisos para ver ninguna configuración."
            }}
        </p>
    </div>
</template>

<script>
import { mapActions, mapState } from "vuex";

import { OperationsModes } from "@/common/fields/operations-modes";
import { isPlanManaged } from "@/common/fields/plan-features";
import { verifyStrictPermission } from "@/common/lib/permissions";

const normalize = (text) =>
    text
        .toLowerCase()
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "");

export default {
    name: "SettingsIndexPage",
    layout: "app",
    data: () => ({
        search: ""
    }),
    computed: {
        ...mapState("auth", ["permissions", "featureFlags"]),
        // Con Productos apagado en "Mis funciones" no tiene sentido elegir
        // tipos de producto (solo tenants del modelo de planes).
        productsOff() {
            return (
                isPlanManaged(this.permissions) &&
                !(this.featureFlags || {}).productsEnabled
            );
        },
        isMultiTenant() {
            return this.$operationMode === OperationsModes.multiTenant;
        },
        items() {
            return [
                {
                    name: "Tipos de producto",
                    description:
                        "Elige qué tipos de producto puede manejar tu catálogo.",
                    icon: "🔷",
                    url: "/app/settings/catalog",
                    scope: "tenant:GetProductTypes",
                    hide: this.productsOff
                },
                {
                    name: "Mis funciones",
                    description:
                        "Activa o apaga las funciones que incluye tu plan: productos, categorías, pedidos y más.",
                    icon: "🧩",
                    url: "/app/settings/features",
                    scope: "tenant:GetFeatureFlagsSettings",
                    hide: !this.isMultiTenant
                },
                {
                    name: "Métodos de pago del portal",
                    description:
                        "Métodos de pago disponibles al crear órdenes internas desde este portal.",
                    icon: "💳",
                    url: "/app/settings/payment-methods",
                    scope: "tenant:GetPaymentMethodsSettings"
                },
                {
                    name: "Tiempos de entrega",
                    description:
                        "Preparación, días de operación, horas de corte y anticipación que alimentan el cálculo de días de entrega.",
                    icon: "⏱️",
                    url: "/app/settings/delivery-schedule",
                    scope: "tenant:GetOrdersSettings"
                },
                {
                    name: "Precios de entrega",
                    description:
                        "Precio de entrega por ciudad y precio por defecto para ciudades no configuradas.",
                    icon: "🚚",
                    url: "/app/settings/delivery-prices",
                    scope: "tenant:GetOrdersSettings"
                },
                {
                    name: "Métodos de pago del checkout",
                    description:
                        "Métodos de pago disponibles para tus clientes en el catálogo público.",
                    icon: "🛒",
                    url: "/app/settings/checkout-payment-methods",
                    scope: "tenant:GetOrdersSettings"
                },
                {
                    name: "Valores mínimos de orden",
                    description:
                        "Monto mínimo global y por tipo de pago para aceptar una orden.",
                    icon: "💲",
                    url: "/app/settings/minimum-order-values",
                    scope: "tenant:GetOrdersSettings"
                },
                {
                    name: "Inventario",
                    description:
                        "Modo de inventario y cómo lo descuentan tus ventas.",
                    icon: "📦",
                    url: "/app/settings/inventory",
                    scope: "tenant:GetInventorySettings"
                },
                {
                    name: "Tags",
                    description:
                        "Catálogo de etiquetas de tu negocio, disponibles para productos y filtros.",
                    icon: "🏷️",
                    url: "/app/settings/tags",
                    scope: "tenant:GetTagsSettings",
                    hide: this.isMultiTenant
                },
                {
                    name: "Filtros del catálogo",
                    description:
                        "Filtros que aparecen en la página general de productos de tu catálogo público.",
                    icon: "🗂️",
                    url: "/app/settings/filters",
                    scope: "tenant:GetFiltersSettings",
                    hide: this.isMultiTenant
                },
                {
                    name: "API Keys",
                    description:
                        "Crea y administra las api keys para conectar sistemas externos con tu tenant.",
                    icon: "🔑",
                    url: "/app/settings/api-keys",
                    scope: "apiKey:List",
                    upgrade: {
                        permission: "upgrade:FeatureApiKeys",
                        feature: "apiKeys"
                    }
                }
            ];
        },
        // Sin el permiso real, una tarjeta con `upgrade` sale con candado
        // solo si el rol tiene ese `upgrade:*` (comparación estricta, mismo
        // criterio que el menú lateral).
        visibleItems() {
            return this.items
                .filter((item) => !item.hide)
                .map((item) => {
                    if (this.$ability.hasPermission(item.scope)) return item;
                    if (
                        item.upgrade &&
                        verifyStrictPermission(
                            this.permissions,
                            item.upgrade.permission
                        )
                    ) {
                        return { ...item, locked: true };
                    }
                    return null;
                })
                .filter(Boolean);
        },
        filteredItems() {
            const query = normalize(this.search.trim());
            if (!query) return this.visibleItems;
            return this.visibleItems.filter((item) =>
                normalize(`${item.name} ${item.description}`).includes(query)
            );
        }
    },
    methods: {
        ...mapActions("platformSubscriptions", ["openPlanUpgrade"]),
        openUpgrade(item) {
            this.openPlanUpgrade({
                kind: "feature",
                feature: item.upgrade.feature
            });
        }
    }
};
</script>
